import { DateTime } from 'luxon'

/**
 * Formatter to format an ISO 8601 datetime string as "hour-ending."
 * @param {string} param0 the hour timestamp from a given EOSTableRow.
 */
export const formatHourEnding = (isoDateTime: string, tz?: string) => {
  const hourEndingDisplayValue = DateTime.fromISO(isoDateTime, { zone: tz }).plus({ minutes: 60 }).toFormat('HH')
  return hourEndingDisplayValue === '00' ? '24' : hourEndingDisplayValue
}

/**
 * Gets the display string for the hour ending tooltip "<hour start> to <hour end>"
 * @param {string} hour the hour to format (expects an ISO 8601 datetime string).
 */
export const getHourEndingDisplayString = (hour: string, tz?: string) => {
  const start = DateTime.fromISO(hour, { zone: tz })
  const end = start.plus({ hours: 1 })
  return `${start.toFormat('HH:mm')} to ${end.toFormat('HH:mm')}`
}

/**
 * Enrollment periods are stored as half-open [start, end) instants at midnight in the site's timezone,
 * while users pick whole calendar days with an inclusive end date. These convert stored instants back to
 * the day the user picked; `tz` falls back to UTC when the site has no timezone.
 */
export const enrollmentStartToDate = (isoDateTime: string, tz?: string): string =>
  DateTime.fromISO(isoDateTime, { zone: tz ?? 'utc' }).toFormat('yyyy-MM-dd')

/** The stored end is exclusive, so the last covered day is the one containing the instant just before it. */
export const enrollmentEndToDate = (isoDateTime: string, tz?: string): string =>
  DateTime.fromISO(isoDateTime, { zone: tz ?? 'utc' })
    .minus({ milliseconds: 1 })
    .toFormat('yyyy-MM-dd')

/** Formats an enrollment's start instant as MM/DD/YYYY. */
export const formatEnrollmentDate = (isoDateTime: string, tz?: string): string =>
  DateTime.fromISO(isoDateTime, { zone: tz ?? 'utc' }).toFormat('MM/dd/yyyy')

/** Formats an enrollment's (exclusive) end instant as its inclusive last day, MM/DD/YYYY. */
export const formatEnrollmentEndDate = (isoDateTime: string, tz?: string): string =>
  DateTime.fromISO(isoDateTime, { zone: tz ?? 'utc' })
    .minus({ milliseconds: 1 })
    .toFormat('MM/dd/yyyy')

/**
 * Formats the time window for a time window.
 * @param {string} startIsoDatetime The start time in ISO 8601 format.
 * @param {string} endIsoDatetime The end time in ISO 8601 format.
 * @returns {string} The formatted time window.
 */
export const formatTimeWindow = (startIsoDatetime: string, endIsoDatetime: string, tz?: string): string => {
  const start = DateTime.fromISO(startIsoDatetime, { zone: tz })
  const end = DateTime.fromISO(endIsoDatetime, { zone: tz })
  return `${start.toFormat('MMM dd HH:mm')} to ${end.toFormat('MMM dd HH:mm')}`
}
